import React from 'react';
import { Loader2 } from 'lucide-react';
import { Theme } from '../../types/theme';
import PhoneInput from "react-phone-input-2";
import { Info } from "lucide-react";
import 'react-phone-input-2/lib/style.css';

export interface BookingDetails {
  name?: string;
  email?: string;
  phone?: string;
}

interface BookingFormProps {
  theme: Theme;
  name: string | null;
  isLoading: boolean;
  mode: string;
  onSubmit: (details: BookingDetails) => void;
}
const isValidEmail = (email: string) => {
  // Basic validation: at least 8 digits
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  return emailRegex.test(email);
};

export const BookingForm: React.FC<BookingFormProps> = ({ theme, name, isLoading, mode, onSubmit }) => {
  const [bookingDetails, setBookingDetails] = React.useState<BookingDetails>({
    name: name || '',
    email: '',
    phone: ''
  });
  const [isEmailValid, setIsEmailValid] = React.useState(false)
  const [isPhoneValid, setIsPhoneValid] = React.useState(false)
  return (
    <div className="rounded-xl relative p-6 border max-w-md" style={{
      backgroundColor: theme.surfaceColor,
      borderColor: theme.borderColor
    }}>
      <div className="flex items-center space-x-2 mb-4">
        <h6 className="text-lg font-light text-center text-gray-500">{mode === 'voice' ?
          <>
            To Access Voice Agent <br />Please provide <span className='font-semibold' style={{ color: theme.primaryColor }}>Contact Details</span> to continue.
          </>
          :
          <>
            Free message limit exceeded! Please provide <span className='font-semibold' style={{ color: theme.primaryColor }}>Contact Details</span> to continue.
          </>
        }
        </h6>
      </div>

      <div className="space-y-4">
        {/* Form Fields */}
        <InputField
          label="Full Name *"
          value={bookingDetails.name || ''}
          onChange={(value) => setBookingDetails({ ...bookingDetails, name: value })}
          theme={theme}
          placeholder="Enter your full name"
        />
        <div className="relative">
          <label className="block text-sm font-medium mb-2" style={{ color: theme.textColor }}>
            Phone Number *
          </label>

          <div className="relative">
            <PhoneInput
              country={"in"}
              value={bookingDetails.phone}
              onChange={(value, countryData: any) => {
                setBookingDetails({
                  ...bookingDetails,
                  phone: value,
                });
                // 1. Calculate the length of the mask (including dots)
                const formatLength = countryData.format.replace(/[+\-\(\)\s]/g, "").length;

                // 2. Calculate the length of the actual digits typed
                const valueLength = value.replace(/\D/g, "").length;

                // 3. Validation Logic
                // Pass if lengths match OR if input hit the max standard limit (15) but format expected more
                const isValid = valueLength === formatLength || (valueLength > 12 && formatLength > 12);

                setIsPhoneValid(isValid);
              }}
              inputStyle={{
                // width: "100%",
                paddingLeft: "48px",
                backgroundColor: theme.backgroundColor,
                borderColor: theme.borderColor,
                color: theme.textColor,
                height: "48px",
                fontSize: "15px"
              }}
              buttonStyle={{
                backgroundColor: theme.surfaceColor,
                borderColor: theme.borderColor
              }}
              placeholder={"Phone number"}
              enableSearch={true}
            />

            {/* ERROR ICON + TOOLTIP */}
            {!isPhoneValid && bookingDetails.phone && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 group cursor-pointer">
                <Info className="w-5 h-5 text-red-500" />

                <span className="absolute right-[-200%] top-full mt-2 opacity-0 group-hover:opacity-100 transition bg-gray-500 text-white text-xs p-2 rounded shadow-lg whitespace-nowrap z-50">
                  Invalid phone number
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="relative">
          <InputField
            label="Email *"
            type="email"
            value={bookingDetails.email || ''}
            onChange={(value) => {
              setBookingDetails({ ...bookingDetails, email: value });
              const isValid = isValidEmail(value)
              setIsEmailValid(isValid)
            }}
            theme={theme}
            placeholder="example@email.com"
          />

          {/* ERROR ICON + TOOLTIP */}
          {!isEmailValid && bookingDetails.email && (
            <div className="absolute right-0 top-11 -translate-x-1/2 group cursor-pointer">
              <Info className="w-5 h-5 text-red-500" />

              <span className="absolute right-[-200%] top-full mt-2 opacity-0 group-hover:opacity-100 transition bg-gray-500 text-white text-xs p-2 rounded shadow-lg whitespace-nowrap z-50">
                Invalid email
              </span>
            </div>
          )}
        </div>

        <button
          onClick={() => onSubmit(bookingDetails)}
          disabled={!bookingDetails.name || !bookingDetails.email || !isPhoneValid || isLoading}
          className="w-full p-3 flex items-center justify-center text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
          style={{ backgroundColor: theme.primaryColor }}
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit"}
        </button>
      </div>
    </div>
  )
};

interface InputFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  theme: Theme;
  placeholder: string;
  type?: string;
}

const InputField: React.FC<InputFieldProps> = ({ label, value, onChange, theme, placeholder, type = "text" }) => (
  <div>
    <label className="block text-sm font-medium mb-2" style={{ color: theme.textColor }}>
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full p-3 border rounded-lg focus:outline-none transition-colors"
      style={{
        backgroundColor: theme.backgroundColor,
        borderColor: theme.borderColor,
        color: theme.textColor
      }}
      placeholder={placeholder}
    />
  </div>
);